import {observer} from 'mobx-react-lite'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {cn} from '@containers/html-utils'
import {useCalendarStore} from '../../calendar-store'
import {hours, minTaskHeight, proportionPixelsDivideOnHour} from '../../utils'
import {daysHeaderBottomLine} from '../all-day/all-day-placeholder'
import {getMaxHeight} from '../all-day/all-day-utils'
import IconExpand from './icon-expand.svg'

const CellTw = tw.div`
  flex
  min-w-full
  relative
  text-11
`

const HourTw = tw.div`
  h-60
  w-64
  grow-0
`

const HourTextTw = tw.div<{$current?: boolean}>`
  absolute
  leading-3
  right-16
  ${p => (p.$current ? 'text-accent-old' : 'text-obscure-old -top-6')}
`

const NoopTw = tw.div`
  border-l-0
  sticky
  top-0
  z-30
  border
  border-r-0
  border-solid
  border-input-disabled
  h-40
  bg-main-old
  border-t-0
`

const AllDaysTw = tw.div`
  text-11
  text-third-old
  flex
  justify-center
  bg-main-old
  min-w-64
`

const ExpandTw = tw.div<{$rotate: boolean}>`
  ${p => p.$rotate && 'rotate-180'}
  text-third-old
  hover:text-action-hover
  active:text-action-pressed
  absolute
  bottom-4
  left-1/2
  transform
  -translate-x-1/2
`

export const getDashLine = () => {
  return (
    <div
      className="h-1 text-accent-old"
      style={{
        background: `repeating-linear-gradient(to right, currentColor 0, currentColor 3px, transparent 3px, transparent 6px)`
      }}
    />
  )
}

export const getStraightLine = () => {
  return (
    <div
      className="h-1 text-accent-old"
      style={{
        background: `repeating-linear-gradient(to right, currentColor 0, currentColor 3px, transparent 3px)`
      }}
    />
  )
}

const formatTwoDigit = (hour: number) => {
  return hour < 10 ? `0${hour}` : hour
}

// eslint-disable-next-line max-lines-per-function
export const Nav = observer(() => {
  const [state, store] = useCalendarStore()
  const [hourTime, minuteTime] = store.currentHourAndMinute
  const top = minuteTime * proportionPixelsDivideOnHour - 1
  const maxHeight = getMaxHeight(state.showMoreAllDayTasks)

  return (
    <div className="sticky left-0 z-40 bg-main-old">
      <NoopTw />
      {state.allDayTasksHeight > 3 && (
        <div className="sticky z-30 top-40" style={{maxHeight}}>
          <AllDaysTw
            style={{
              height: state.allDayTasksHeight,
              maxHeight
            }}
          >
            <div className="absolute top-2">{Lang.msg.calendar.allDay}</div>
            {state.maxTasksInColHeight > minTaskHeight * 4 && (
              <div
                className="w-full h-full cursor-pointer"
                onClick={() => store.toggleShowMoreAllDayTasks()}
              >
                <ExpandTw $rotate={state.showMoreAllDayTasks}>
                  <IconExpand />
                </ExpandTw>
              </div>
            )}
            <div className="absolute w-full -bottom-3">
              {daysHeaderBottomLine}
            </div>
          </AllDaysTw>
        </div>
      )}

      <div className={cn('my-16', state.maxTasksInColHeight > 0 && 'pt-3')}>
        {hours.map(hour => (
          <CellTw key={hour}>
            {hour === hourTime && (
              <span
                className="absolute right-0 w-6"
                style={{
                  top,
                  lineHeight: 0
                }}
              >
                {getDashLine()}
              </span>
            )}
            <HourTw>
              {hour === hourTime && minuteTime > 30 && (
                <HourTextTw>{formatTwoDigit(hour)}:00</HourTextTw>
              )}
              {hour === hourTime ? (
                <HourTextTw
                  $current
                  style={{
                    top,
                    lineHeight: 0
                  }}
                >
                  {formatTwoDigit(hourTime)}:{formatTwoDigit(minuteTime)}
                </HourTextTw>
              ) : (
                <>
                  {hour === hourTime + 1 && minuteTime > 30 ? null : (
                    <HourTextTw>{formatTwoDigit(hour)}:00</HourTextTw>
                  )}
                </>
              )}
            </HourTw>
          </CellTw>
        ))}
      </div>
    </div>
  )
})
